import { useTranslation } from 'react-i18next';
import { Link2, Plus, Settings, Layers } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { AssociatedProductsDisplay } from './AssociatedProductsDisplay';
import type { ProductAssociations } from '@/lib/types';

interface ProductGroup {
  name: string;
  refId?: string;
  productCount?: number;
}

interface ProductAssociationsSectionProps {
  associations?: ProductAssociations;
  group?: ProductGroup;
  productRefId: string;
  onManageClick: () => void;
}

export function ProductAssociationsSection({
  associations,
  group,
  productRefId,
  onManageClick,
}: ProductAssociationsSectionProps) {
  const { t } = useTranslation();

  const hasAssociatedProducts = associations &&
    ((associations.items && associations.items.length > 0) ||
     (associations.products && associations.products.length > 0));

  return (
    <>
      {/* Group Card */}
      {group && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="size-5" />
              {t('products.productGroup')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
              <div>
                <p className="font-medium">{group.name}</p>
                {group.productCount && (
                  <p className="text-xs text-muted-foreground">
                    {group.productCount} {t('products.productsInGroup')}
                  </p>
                )}
              </div>
              <Badge variant="outline">{group.refId}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Associated Products Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="size-5" />
                {t('products.associatedProducts')}
              </CardTitle>
              {associations && (
                <p className="text-sm text-muted-foreground mt-1">
                  {associations.name}
                  {associations.description && ` - ${associations.description}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {associations?.products && associations.products.length > 0 && (
                <Badge variant="secondary">
                  {associations.products.length} {t('products.productsCount')}
                </Badge>
              )}
              <Button variant="outline" size="sm" onClick={onManageClick}>
                {associations ? (
                  <>
                    <Settings className="size-4 mr-1" />
                    {t('associations.manageAssociations')}
                  </>
                ) : (
                  <>
                    <Plus className="size-4 mr-1" />
                    {t('associations.manageAssociations')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {hasAssociatedProducts ? (
            <AssociatedProductsDisplay
              associations={associations}
              currentProductId={productRefId}
            />
          ) : (
            <div className="text-center py-8">
              <Link2 className="size-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">{t('associations.noGroups')}</p>
              <Button variant="outline" onClick={onManageClick}>
                <Plus className="size-4 mr-2" />
                {t('associations.createNewGroup')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
